import { useState, useRef } from 'react'
import { Icon } from './Layout'
import { usePlatformSettings } from '../context/PlatformSettingsContext'
import { convertCptToCis } from '../services/profiles'
import { humanizeApiError } from '../services/client'

// A failed request that never reached the backend (offline, timeout, CORS,
// etc.) never gets a response, so formatApiError in client.js never sets
// err.status. A rejection the backend actually returned (400/404) always
// has one. This distinction is what decides whether a retry may reuse the
// same idempotencyKey.
function isNetworkError(err) {
  return err?.status === undefined
}

export default function CptConversionPanel({ cptBalance = 0, onConverted }) {
  const { isFeatureEnabled, cisRewards } = usePlatformSettings()
  const enabled = isFeatureEnabled('cptToCisConversion')
  const rate = Number(cisRewards?.cptToCisRate ?? 40)
  const [amountInput, setAmountInput] = useState('')
  const [converting, setConverting] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const attemptRef = useRef(null)

  if (!enabled) {
    return (
      <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-3 lg:p-6">
        <h2 className="font-headline-md text-sm lg:text-headline-md uppercase font-black flex items-center gap-2 mb-2">
          <Icon name="sync_alt" className="text-on-surface-variant text-[18px] lg:text-[22px]" />
          CPT → CIS Conversion
        </h2>
        <p className="text-on-surface-variant py-2 text-sm">
          Converting CPT into CIS isn't open yet — check back once an admin turns it on.
        </p>
      </section>
    )
  }

  const parsedAmount = Number(amountInput)
  const hasValidAmount = amountInput.trim() !== '' && Number.isFinite(parsedAmount) && parsedAmount > 0
  const exceedsBalance = hasValidAmount && parsedAmount > cptBalance
  const cisPreview = hasValidAmount ? parsedAmount * rate : 0

  const handleAmountChange = (e) => {
    setAmountInput(e.target.value)
    setError(null)
    setSuccessMessage(null)
  }

  const handleConvert = async () => {
    if (!hasValidAmount || exceedsBalance || converting) return

    const reuse =
      attemptRef.current &&
      attemptRef.current.networkError &&
      attemptRef.current.amount === parsedAmount
    const idempotencyKey = reuse ? attemptRef.current.key : crypto.randomUUID()
    attemptRef.current = { key: idempotencyKey, amount: parsedAmount, networkError: false }

    setConverting(true)
    setError(null)
    setSuccessMessage(null)
    try {
      const ledger = await convertCptToCis(parsedAmount, idempotencyKey)
      const cptSpent = Math.abs(Number(ledger.amount ?? parsedAmount))
      const cisCredited = cisPreview
      attemptRef.current = null
      setAmountInput('')
      setSuccessMessage(`Converted ${cptSpent.toLocaleString()} CPT into ${cisCredited.toLocaleString()} CIS.`)
      onConverted?.({ cptSpent, cisCredited, ledger })
    } catch (err) {
      if (isNetworkError(err)) {
        attemptRef.current = { key: idempotencyKey, amount: parsedAmount, networkError: true }
      } else {
        attemptRef.current = null
      }
      setError(humanizeApiError(err))
    } finally {
      setConverting(false)
    }
  }

  return (
    <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-3 lg:p-6">
      <div className="flex items-center justify-between mb-3 lg:mb-4">
        <h2 className="font-headline-md text-sm lg:text-headline-md uppercase font-black flex items-center gap-2">
          <Icon name="sync_alt" className="text-primary-container text-[18px] lg:text-[22px]" />
          CPT → CIS Conversion
        </h2>
      </div>

      <p className="text-[11px] lg:text-[12px] text-on-surface-variant mb-3 lg:mb-4">
        Convert your CPT into CIS at the current rate of 1 CPT = {rate.toLocaleString()} CIS. The rate is
        admin-configurable and may change.
      </p>

      <div className="flex items-stretch gap-2 mb-2">
        <input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          value={amountInput}
          onChange={handleAmountChange}
          placeholder="CPT amount"
          disabled={converting}
          className="flex-1 bg-background border border-on-background/15 lg:neo-border px-3 py-2 lg:py-3 font-mono text-[13px] disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => setAmountInput(String(cptBalance))}
          disabled={converting || cptBalance <= 0}
          className="shrink-0 px-3 py-2 lg:py-3 border border-on-background/15 lg:neo-border bg-background font-bold text-[10px] lg:text-[11px] uppercase tracking-wider hover:bg-primary-container/10 disabled:opacity-50"
        >
          Max
        </button>
        <button
          type="button"
          onClick={handleConvert}
          disabled={!hasValidAmount || exceedsBalance || converting}
          className="shrink-0 flex items-center gap-1.5 px-3 lg:px-4 py-2 lg:py-3 bg-primary-container text-on-primary-fixed font-bold text-[11px] lg:text-[12px] uppercase tracking-wider hover:brightness-105 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
        >
          {converting ? (
            <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent animate-spin rounded-full" />
          ) : (
            <Icon name="swap_horiz" className="text-[14px] lg:text-[16px]" />
          )}
          Convert
        </button>
      </div>

      <div className="flex items-center justify-between text-[10px] lg:text-[11px] font-mono text-on-surface-variant mb-2">
        <span>Balance: {Number(cptBalance).toLocaleString()} CPT</span>
        {hasValidAmount && (
          <span>
            ≈ <strong className="text-primary-container">{cisPreview.toLocaleString(undefined, { maximumFractionDigits: 4 })} CIS</strong>
          </span>
        )}
      </div>

      {exceedsBalance && (
        <p className="text-[10px] lg:text-[11px] text-error font-bold">Amount exceeds your current CPT balance.</p>
      )}
      {error && (
        <p className="text-[10px] lg:text-[11px] text-error font-bold">{error}</p>
      )}
      {successMessage && (
        <p className="text-[10px] lg:text-[11px] text-green-600 font-bold">{successMessage}</p>
      )}
    </section>
  )
}